import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useAuth, type AuthUser } from "../auth/AuthProvider";
import { apolloClient } from "../apollo/client";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, setUser } = useAuth();
  const redirectTo =
    (location.state as { from?: string } | null)?.from || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, user, navigate, redirectTo]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.error || "Sign in failed. Please try again.");
        setSubmitting(false);
        return;
      }

      setUser(data.user as AuthUser);
      // New identity on this browser: drop any cached data from a previous
      // session and refetch active queries as this user.
      try {
        await apolloClient.resetStore();
      } catch {
        // resetStore() rejects if a refetched query errors; ignore so the
        // sign-in still proceeds.
      }
      navigate(redirectTo, { replace: true });
    } catch {
      setError("Could not reach the server. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-paper text-ink antialiased pt-[var(--banner-h)]">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[140px] -top-[200px] h-[520px] w-[520px] rounded-full bg-tide/10 blur-[80px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[240px] -right-[120px] h-[460px] w-[460px] rounded-full bg-spark/10 blur-[80px]"
      />
      <div
        aria-hidden
        className="login-horizon pointer-events-none fixed inset-x-0 bottom-0 z-0 h-[280px] overflow-hidden"
      >
        <span
          className="portal-sheen absolute bottom-0 left-0 h-px w-[35%]"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(74, 142, 224, 0.7) 50%, transparent 100%)",
          }}
        />
        <span
          className="portal-sheen-2 absolute bottom-[14px] left-0 h-px w-[28%]"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(168, 136, 76, 0.5) 50%, transparent 100%)",
          }}
        />
      </div>

      <nav className="relative z-20 flex items-center justify-between px-8 pt-7 md:px-14">
        <div className="flex items-center gap-2.5">
          <img
            src="/reflex-mark.svg"
            alt=""
            width="32"
            height="32"
            className="h-8 w-8"
          />
          <span className="text-[15px] font-semibold tracking-tight">
            Reflex Labs
          </span>
        </div>
        <a
          href="mailto:hello@reflexlabs.io"
          className="text-[13px] text-ink-4 transition hover:text-ink"
        >
          Need help?
        </a>
      </nav>

      <main className="relative z-10 flex min-h-[calc(100vh-140px)] items-center justify-center px-6 py-12">
        <div className="portal-reveal portal-reveal-1 w-full max-w-[400px]">
          <div className="mb-7 text-center">
            <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
              Sign in to{" "}
              <em className="font-normal italic text-tide">your practice</em>.
            </h1>
          </div>

          <div className="login-card-shadow rounded-2xl bg-paper-hi p-8">
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div>
                <label
                  htmlFor="portal-email"
                  className="mb-2 block text-[11.5px] font-semibold uppercase tracking-wider text-ink-4"
                >
                  Email
                </label>
                <input
                  id="portal-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-ink/10 bg-paper px-4 py-3 text-[14.5px] text-ink transition placeholder:text-ink-4/60 focus:border-tide focus:outline-none focus:ring-[3px] focus:ring-tide/15"
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label
                    htmlFor="portal-password"
                    className="block text-[11.5px] font-semibold uppercase tracking-wider text-ink-4"
                  >
                    Password
                  </label>
                  <Link
                    to="/reset-password"
                    className="text-[11.5px] font-medium text-tide hover:underline"
                  >
                    Forgot?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="portal-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-ink/10 bg-paper px-4 py-3 pr-10 text-[14.5px] text-ink transition placeholder:text-ink-4/60 focus:border-tide focus:outline-none focus:ring-[3px] focus:ring-tide/15"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-4 transition hover:text-ink"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-rust/30 bg-rust-s/40 px-3 py-2 text-[13px] text-rust"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-tide px-3 py-3 text-[14.5px] font-medium text-paper-hi transition hover:-translate-y-px hover:bg-tide-d hover:shadow-[0_10px_30px_-8px_rgba(38,99,199,0.35)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:bg-tide disabled:hover:shadow-none"
              >
                {submitting ? "Signing in…" : "Sign in"}
                {!submitting && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                )}
              </button>
            </form>
          </div>

          <div className="mt-5 text-center text-[12.5px] text-ink-4">
            Need an account?{" "}
            <a
              href="mailto:hello@reflexlabs.io"
              className="font-medium text-tide hover:underline"
            >
              Contact Reflex Labs
            </a>
            .
          </div>
        </div>
      </main>

      <footer className="relative z-10 flex items-center justify-between px-8 py-6 text-[11.5px] text-ink-4 md:px-14">
        <span>© {new Date().getFullYear()} Reflex Labs</span>
        <div className="flex items-center gap-4">
          <a href="#" className="hover:text-ink-3">
            Privacy
          </a>
          <a href="#" className="hover:text-ink-3">
            Terms
          </a>
          <a href="#" className="hover:text-ink-3">
            Status
          </a>
        </div>
      </footer>
    </div>
  );
}
